<template>
    <Transition name="collapse"
                @enter="onEnter"
                @after-enter="onAfterEnter"
                @leave="onLeave"
                @after-leave="onAfterLeave">
        <div v-show="show"
             data-component="CollapseTransition"
             class="collapse-transition">
            <slot></slot>
        </div>
    </Transition>
</template>

<script>
/**
 * Slides its content open and closed by animating the height of a wrapper around it.
 *
 * The wrapper has no padding or border of its own, so content with either of those collapses all
 * the way down to nothing.
 */
export default {
    name: 'CollapseTransition',

    props: {
        show: {
            type: Boolean,
            default: false,
        },
        // How long the slide takes. Worth shortening when something is waiting for the content to
        // finish opening (e.g. a scroll to it).
        durationMs: {
            type: Number,
            default: 200,
        },
    },

    // Emitted once the content has finished sliding, for anything that has to wait for the final
    // size (e.g. scrolling the content into view).
    emits: ['opened', 'closed'],

    methods: {
        /**
         * Grow from nothing to the height of the content.
         *
         * @param {HTMLElement} el
         */
        onEnter(el) {
            el.style.transitionDuration = `${this.durationMs}ms`;
            el.style.height = '0';
            this.forceReflow(el);
            el.style.height = `${el.scrollHeight}px`;
        },

        /**
         * Back to an automatic height, so the content can grow and shrink freely while open.
         *
         * @param {HTMLElement} el
         */
        onAfterEnter(el) {
            el.style.height = '';
            el.style.transitionDuration = '';
            this.$emit('opened');
        },

        /**
         * Shrink from the height of the content down to nothing.
         *
         * @param {HTMLElement} el
         */
        onLeave(el) {
            el.style.transitionDuration = `${this.durationMs}ms`;
            el.style.height = `${el.scrollHeight}px`;
            this.forceReflow(el);
            el.style.height = '0';
        },

        /**
         * @param {HTMLElement} el
         */
        onAfterLeave(el) {
            el.style.height = '';
            el.style.transitionDuration = '';
            this.$emit('closed');
        },

        /**
         * Reading a layout property makes the browser apply the height that was just set, so the
         * change to the next height animates instead of jumping straight to it.
         *
         * @param {HTMLElement} el
         *
         * @return {number}
         */
        forceReflow(el) {
            return el.offsetHeight;
        },
    },
};
</script>

<style lang="less" scoped>
.collapse-enter-active,
.collapse-leave-active {
    overflow: hidden;
    // The duration is set on the element by the hooks, from the durationMs prop. This one is only
    // a fallback and should match that prop's default.
    transition: height 0.2s ease-out;
}
</style>
